// src/pages/CancerPage.jsx
// Reusable evidence-based cancer guide page with SEO optimization & 10/10 clinical UX

import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { trackPageView, trackScrollDepth } from '@/utils/seo/analytics';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import '@/App.css';
import BrandLogo from '@/components/brand/BrandLogo';
import Footer from '@/components/Footer';
import { 
  FindHospitalsCTA,
  WhatsAppCTA, 
  CostCalculatorCTA, 
  SecondOpinionCTA,
  ConversionCTACard,
  StickyCTABar
} from '@/components/ConversionCTAs';
import { cancerPagesData } from '@/data/cancerPagesData';
import { SITE_ORIGIN } from '@/config/site';
import { 
  AlertCircle, 
  Stethoscope, 
  DollarSign, 
  Building2,
  CheckCircle2,
  ArrowRight,
  ShieldCheck,
  Calendar,
  UserCheck,
  ChevronRight,
  ExternalLink,
  Layers,
  HeartPulse
} from 'lucide-react';

export default function CancerPage() {
  const { cancerType } = useParams();
  const navigate = useNavigate();
  const data = cancerPagesData[cancerType];

  // Fallback if cancer type not found
  if (!data) {
    return (
      <div className="min-h-screen bg-[#FBFBFA] flex items-center justify-center p-6 text-slate-900">
        <div className="text-center max-w-md bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-6 h-6" />
          </div>
          <h1 className="text-2xl font-bold font-serif mb-2">Cancer Guide Not Found</h1>
          <p className="text-slate-600 text-sm mb-6">
            The requested cancer type page is currently not available or has moved.
          </p>
          <Link
            to="/cancer"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-forest-ink text-white font-semibold text-sm hover:bg-forest-shadow transition"
          >
            <span>Browse All Cancer Guides</span>
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const { name, slug, meta, content, keywords, sources, reviewedBy, reviewedDate, updatedDate } = data;
  const baseUrl = SITE_ORIGIN;
  const pageUrl = `${baseUrl}/cancer/${slug}`;

  useEffect(() => {
    trackPageView(pageUrl, `${name} Treatment & Clinical Guide | ByOnco`);
    trackScrollDepth();
  }, [pageUrl, name]);

  // Generate JSON-LD Schema
  const generateSchema = () => {
    const medicalConditionSchema = {
      "@context": "https://schema.org",
      "@type": "MedicalCondition",
      "name": name,
      "alternateName": content.h1,
      "description": meta.description,
      "url": pageUrl,
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": pageUrl
      }
    };

    const faqSchema = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": content.faq.map(item => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      }))
    };

    const breadcrumbSchema = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": baseUrl
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "Cancer Types",
          "item": `${baseUrl}/cancer`
        },
        {
          "@type": "ListItem",
          "position": 3,
          "name": name,
          "item": pageUrl
        }
      ]
    };

    const organizationSchema = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "ByOnco",
      "url": baseUrl,
      "logo": `${baseUrl}/byonco-logo.png`,
      "description": "Cancer care navigation that helps patients find the right centers"
    };

    return [medicalConditionSchema, faqSchema, breadcrumbSchema, organizationSchema];
  };

  const schemas = generateSchema();

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{meta.title}</title>
        <meta name="title" content={meta.title} />
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={pageUrl} />

        {/* Hreflang Tags */}
        <link rel="alternate" hreflang="en-IN" href={`${pageUrl}?locale=en-IN`} />
        <link rel="alternate" hreflang="en-US" href={`${pageUrl}?locale=en-US`} />
        <link rel="alternate" hreflang="x-default" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:image" content={meta.ogImage || `${SITE_ORIGIN}/preview.png`} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content={`${name} - Treatment Information | ByOnco`} />
        <meta property="og:site_name" content="ByOnco" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.ogImage || `${SITE_ORIGIN}/preview.png`} />
        <meta name="twitter:image:alt" content={`${name} - Treatment Information | ByOnco`} />

        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="English" />
        <meta name="author" content="PraesidioCare Private Limited" />
        <meta name="keywords" content={[
          ...(keywords?.india?.symptoms || []),
          ...(keywords?.india?.diagnosis || []),
          ...(keywords?.india?.treatment || []),
          ...(keywords?.india?.cost || []),
          ...(keywords?.india?.hospital || []),
          ...(keywords?.us?.symptoms || []),
          ...(keywords?.us?.diagnosis || []),
          ...(keywords?.us?.treatment || []),
          ...(keywords?.us?.cost || []),
          ...(keywords?.us?.hospital || [])
        ].join(", ")} />

        {/* JSON-LD Schemas */}
        {schemas.map((schema, index) => (
          <script
            key={index}
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
          />
        ))}
      </Helmet>

      {/* Main Canvas: Warm Cream, Soothing Light Botanical Theme */}
      <div className="min-h-screen bg-[#FBFBFA] text-slate-900 font-sans antialiased selection:bg-emerald-100 selection:text-emerald-900">
        
        {/* Sticky Header with Frosted Glass & Brand Logo */}
        <header className="bg-white/85 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3.5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BrandLogo size="md" />
              <div className="hidden sm:flex items-center space-x-2 text-xs font-medium text-slate-500">
                <span className="text-slate-300">/</span>
                <Link to="/cancer" className="hover:text-forest-ink transition">Cancer Guides</Link>
                <span className="text-slate-300">/</span>
                <span className="text-emerald-950 font-semibold">{name}</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Link
                to="/cancer"
                className="hidden md:inline-flex text-xs font-medium text-slate-600 hover:text-forest-ink transition"
              >
                All Guides
              </Link>
              <Link
                to="/get-started"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-forest-ink hover:bg-forest-shadow text-white text-xs sm:text-sm font-semibold transition shadow-xs"
              >
                <span>Get Matched Care</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="relative pt-10 pb-12 bg-gradient-to-b from-emerald-50/60 via-[#FBFBFA] to-[#FBFBFA] border-b border-slate-200/70">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Breadcrumb row */}
            <nav aria-label="Breadcrumb" className="mb-4">
              <ol className="flex items-center space-x-2 text-xs text-slate-500">
                <li>
                  <Link to="/" className="hover:text-forest-ink transition">Home</Link>
                </li>
                <li className="text-slate-300">/</li>
                <li>
                  <Link to="/cancer" className="hover:text-forest-ink transition">Cancer Types</Link>
                </li>
                <li className="text-slate-300">/</li>
                <li className="text-emerald-900 font-semibold" aria-current="page">{name}</li>
              </ol>
            </nav>

            {/* Trust Pill */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-100/80 border border-emerald-200 text-emerald-900 text-xs font-semibold mb-4"
            >
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-700" />
              <span>NCCN Guideline Aligned · Evidence-Based Guide</span>
            </motion.div>

            {/* Title & Overview */}
            <motion.h1
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-3xl sm:text-4xl lg:text-5xl font-bold font-serif text-forest-ink tracking-tight mb-4 leading-tight"
            >
              {content.h1}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base sm:text-lg text-slate-600 max-w-3xl leading-relaxed mb-8"
            >
              {content.intro}
            </motion.p>

            {/* Quick Action Pill Bar */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 max-w-4xl"
            >
              <FindHospitalsCTA text="Find Hospitals" location="hero" />
              <WhatsAppCTA text="24/7 Care Chat" location="hero" />
              <CostCalculatorCTA text="Estimate Cost" location="hero" />
              <SecondOpinionCTA text="Second Opinion" location="hero" />
            </motion.div>
          </div>
        </section>

        {/* Main Content Body */}
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
          
          {/* Symptoms Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-rose-50 text-rose-700 flex items-center justify-center flex-shrink-0">
                  <AlertCircle className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900">
                    {content.symptoms.title}
                  </h2>
                  <p className="text-xs text-slate-400">Common indicators and clinical warning signs</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 mb-6">
                {content.symptoms.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-3 p-3.5 rounded-xl bg-slate-50/70 border border-slate-200/60">
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span className="text-sm font-medium text-slate-800 leading-snug">{item}</span>
                  </div>
                ))}
              </div>

              {content.symptoms.note && (
                <div className="bg-sky-50/80 border border-sky-200/80 rounded-xl p-4 flex items-start gap-3">
                  <ShieldCheck className="w-5 h-5 text-sky-700 mt-0.5 flex-shrink-0" />
                  <p className="text-xs sm:text-sm text-sky-950 leading-relaxed font-medium">
                    {content.symptoms.note}
                  </p>
                </div>
              )}
            </div>
          </motion.section>

          {/* Diagnosis Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center flex-shrink-0">
                  <Stethoscope className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900">
                    {content.diagnosis.title}
                  </h2>
                  <p className="text-xs text-slate-400">Step-by-step clinical evaluation and staging workflow</p>
                </div>
              </div>

              <div className="space-y-3">
                {content.diagnosis.steps.map((step, index) => (
                  <div key={index} className="flex items-start gap-4 p-4 rounded-xl bg-slate-50/60 border border-slate-200/70">
                    <span className="w-7 h-7 rounded-full bg-emerald-100 text-forest-ink font-bold text-xs flex items-center justify-center flex-shrink-0 mt-0.5">
                      {index + 1}
                    </span>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed">
                      {step}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Staging Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center flex-shrink-0">
                  <Layers className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900">
                    Staging Explained
                  </h2>
                  <p className="text-xs text-slate-400">How tumor size, lymph node involvement, and metastasis are graded</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {content.staging.stages.map((stage, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-xl bg-gradient-to-br from-emerald-50/50 to-white border border-emerald-100 shadow-xs flex flex-col justify-between"
                  >
                    <div>
                      <span className="inline-block px-3 py-1 rounded-md bg-forest-ink text-white font-bold text-xs mb-3 shadow-xs">
                        {stage.stage}
                      </span>
                      <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                        {stage.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Treatment Options */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center flex-shrink-0">
                  <HeartPulse className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900">
                    {content.treatment.title}
                  </h2>
                  <p className="text-xs text-slate-400">Standard of care modalities according to NCCN clinical guidelines</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {content.treatment.options.map((option, index) => (
                  <div
                    key={index}
                    className="p-5 rounded-xl bg-slate-50/60 border border-slate-200/80 hover:border-emerald-600/40 hover:bg-white hover:shadow-sm transition-all flex flex-col justify-between"
                  >
                    <div>
                      <h3 className="font-bold font-serif text-base text-slate-900 mb-2">
                        {option.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                        {option.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.section>

          {/* Treatment Cost Comparison Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.5 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-emerald-50 text-forest-ink flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900">
                    Treatment Cost Comparisons
                  </h2>
                  <p className="text-xs text-slate-400">Transparent out-of-pocket benchmarks for India and US healthcare centers</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                
                {/* India Costs Card */}
                <div className="p-5 sm:p-6 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🇮🇳</span>
                    <h3 className="font-bold text-base sm:text-lg text-slate-900">
                      {content.cost.india.title}
                    </h3>
                  </div>

                  <ul className="space-y-2.5 mb-5">
                    {content.cost.india.ranges.map((range, index) => (
                      <li key={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                        <span className="w-2 h-2 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0" />
                        <span className="font-medium">{range}</span>
                      </li>
                    ))}
                  </ul>

                  {content.cost.india.note && (
                    <div className="p-3.5 rounded-lg bg-emerald-50/80 border border-emerald-200/70 text-xs text-emerald-950 leading-relaxed">
                      {content.cost.india.note}
                    </div>
                  )}
                </div>

                {/* US Costs Card */}
                <div className="p-5 sm:p-6 rounded-xl bg-slate-50 border border-slate-200">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="text-2xl">🇺🇸</span>
                    <h3 className="font-bold text-base sm:text-lg text-slate-900">
                      {content.cost.us.title}
                    </h3>
                  </div>

                  <ul className="space-y-2.5 mb-5">
                    {content.cost.us.ranges.map((range, index) => (
                      <li key={index} className="flex items-start gap-2.5 text-xs sm:text-sm text-slate-700">
                        <span className="w-2 h-2 rounded-full bg-emerald-600 mt-1.5 flex-shrink-0" />
                        <span className="font-medium">{range}</span>
                      </li>
                    ))}
                  </ul>

                  {content.cost.us.note && (
                    <div className="p-3.5 rounded-lg bg-emerald-50/80 border border-emerald-200/70 text-xs text-emerald-950 leading-relaxed">
                      {content.cost.us.note}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </motion.section>

          {/* Conversion CTA Card */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <ConversionCTACard cancerType={name} />
          </motion.section>

          {/* Frequently Asked Questions */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.7 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs p-6 sm:p-8">
              <h2 className="text-xl sm:text-2xl font-bold font-serif text-slate-900 mb-6 pb-4 border-b border-slate-100">
                Frequently Asked Questions about {name}
              </h2>

              <Accordion type="single" collapsible className="w-full space-y-3">
                {content.faq.map((item, index) => (
                  <AccordionItem 
                    key={index} 
                    value={`item-${index}`} 
                    className="border border-slate-200/80 rounded-xl px-4 bg-slate-50/40"
                  >
                    <AccordionTrigger className="text-left text-sm sm:text-base font-semibold text-slate-900 hover:text-forest-ink py-4">
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-xs sm:text-sm text-slate-600 leading-relaxed pb-4 pt-1">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </motion.section>

          {/* Medical Disclaimer & Review Credentials */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.8 }}
          >
            <div className="bg-amber-50/70 border border-amber-200/80 rounded-2xl p-6 sm:p-8">
              <div className="flex items-center gap-2.5 mb-2 text-amber-900 font-bold text-base">
                <ShieldCheck className="w-5 h-5 text-amber-700 flex-shrink-0" />
                <span>Medical Integrity & Clinical Disclaimer</span>
              </div>
              <p className="text-xs sm:text-sm text-amber-900/80 leading-relaxed mb-4">
                This guide provides verified clinical insights based on established oncological literature (NCCN, ASCO, ESMO). It is not a substitute for professional medical diagnosis or personalized treatment planning. Always consult your qualified oncology specialist before initiating treatment.
              </p>

              {(reviewedBy || reviewedDate || updatedDate) && (
                <div className="pt-4 border-t border-amber-200/60 flex flex-wrap items-center gap-6 text-xs text-amber-800">
                  {reviewedBy && (
                    <div className="flex items-center gap-1.5">
                      <UserCheck className="w-4 h-4 text-amber-700" />
                      <span>Reviewed by: <strong className="font-semibold text-amber-950">{reviewedBy}</strong></span>
                    </div>
                  )}
                  {reviewedDate && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-amber-700" />
                      <span>Review Date: {new Date(reviewedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                  {updatedDate && (
                    <div className="flex items-center gap-1.5">
                      <Calendar className="w-4 h-4 text-amber-700" />
                      <span>Last Updated: {new Date(updatedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.section>

          {/* Sources Section */}
          {sources && sources.length > 0 && (
            <motion.section
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.85 }}
            >
              <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6">
                <h3 className="text-base font-bold font-serif text-slate-900 mb-3">
                  Sources & Clinical Citations
                </h3>
                <ul className="space-y-2">
                  {sources.map((source, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-emerald-700 mt-1">•</span>
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs sm:text-sm text-slate-600 hover:text-forest-ink hover:underline transition inline-flex items-center gap-1"
                      >
                        <span>{source.title}</span>
                        <ExternalLink className="w-3 h-3 text-slate-400" />
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </motion.section>
          )}

          {/* Related Navigation Resources */}
          <motion.section
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.9 }}
          >
            <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-6 sm:p-8">
              <h3 className="text-lg font-bold font-serif text-slate-900 mb-4">
                Related Oncology Resources
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <Link
                  to="/find-hospitals"
                  className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 hover:bg-emerald-50/70 border border-slate-200/70 hover:border-emerald-600/40 transition group"
                >
                  <div className="flex items-center gap-2.5">
                    <Building2 className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-forest-ink">
                      Find Hospitals
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 group-hover:text-forest-ink transition" />
                </Link>

                <Link
                  to="/cost-calculator"
                  className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 hover:bg-emerald-50/70 border border-slate-200/70 hover:border-emerald-600/40 transition group"
                >
                  <div className="flex items-center gap-2.5">
                    <DollarSign className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-forest-ink">
                      Cost Calculator
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 group-hover:text-forest-ink transition" />
                </Link>

                <Link
                  to="/second-opinion"
                  className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 hover:bg-emerald-50/70 border border-slate-200/70 hover:border-emerald-600/40 transition group"
                >
                  <div className="flex items-center gap-2.5">
                    <Stethoscope className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-forest-ink">
                      Second Opinion
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 group-hover:text-forest-ink transition" />
                </Link>

                <Link
                  to="/rare-cancers"
                  className="flex items-center justify-between p-3.5 rounded-xl bg-slate-50 hover:bg-emerald-50/70 border border-slate-200/70 hover:border-emerald-600/40 transition group"
                >
                  <div className="flex items-center gap-2.5">
                    <ShieldCheck className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-800 group-hover:text-forest-ink">
                      Rare Cancers
                    </span>
                  </div>
                  <ChevronRight className="h-4 w-4 text-slate-400 group-hover:translate-x-1 group-hover:text-forest-ink transition" />
                </Link>
              </div>
            </div>
          </motion.section>
        </main>

        {/* Global Botanical ByOnco Site Footer */}
        <Footer />

        {/* Sticky CTA Bar (Mobile only) */}
        <StickyCTABar cancerType={name} />
      </div>
    </>
  );
}
